import { useEffect, useMemo, useState } from "react";
import type {
  KonsolidiertePosition,
  Preise,
  PreisPosition,
} from "@bidi-handel/core/schemas";
import { api } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { Icon } from "../../shell/Icon";
import { PreisEditDialog } from "./PreisEditDialog";
import {
  buildOzGroups,
  countItems,
  type OzGroupNode,
} from "./oz-tree";

/**
 * Liefert eine Map positionId → KonsolidiertePosition mit OZ, originaltext,
 * menge, einheit, hauptgruppe — alles was die Preise-Liste fuer Hierarchie
 * und Anzeige braucht.
 */
function useKonsolidiertMap(tenantId: string, anfrageId: string) {
  const [map, setMap] = useState<Map<string, KonsolidiertePosition>>(new Map());
  useEffect(() => {
    let cancelled = false;
    fetch(
      `/api/tenants/${encodeURIComponent(tenantId)}/anfragen/${encodeURIComponent(
        anfrageId
      )}/phase/3`
    )
      .then(async (r) => {
        if (!r.ok) return null;
        return (await r.json()) as {
          positionen: KonsolidiertePosition[];
        };
      })
      .then((data) => {
        if (cancelled || !data) return;
        setMap(new Map(data.positionen.map((p) => [p.positionId, p])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId, anfrageId]);
  return map;
}

export function PreiseTab({
  tenantId,
  anfrageId,
}: {
  tenantId: string;
  anfrageId: string;
}) {
  const { state, refetch } = useApi(
    () => api.getPreise(tenantId, anfrageId),
    [tenantId, anfrageId]
  );
  const [local, setLocal] = useState<Preise | null>(null);
  const [editing, setEditing] = useState<PreisPosition | null>(null);
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set());
  const konsolMap = useKonsolidiertMap(tenantId, anfrageId);

  useEffect(() => {
    if (state.status === "ok") setLocal(null);
  }, [state.status, state.data]);

  const preise: Preise | null = local ?? (state.status === "ok" ? state.data : null);
  // Tree-Bau muss VOR den early-returns laufen, damit die Hook-Order stabil
  // bleibt (Rules of Hooks).
  const tree = useMemo(() => {
    if (!preise) return [];
    const enriched = preise.positionen.map((p) => ({
      preis: p,
      konsol: konsolMap.get(p.positionId) ?? null,
    }));
    return buildOzGroups(
      enriched,
      (e) => e.konsol?.oz ?? e.preis.positionId,
      (e) => e.konsol?.hauptgruppe ?? undefined
    );
  }, [preise, konsolMap]);

  if (state.status === "loading" && !local) {
    return (
      <div className="ueber">
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini">// PREISE LADEN …</span>
          </div>
        </section>
      </div>
    );
  }

  if (state.status === "error" && !local) {
    return (
      <div className="ueber">
        <section className="block" style={{ borderColor: "oklch(0.72 0.18 25 / 0.4)" }}>
          <div className="block-head">
            <span className="uppercase-mini" style={{ color: "var(--bad)" }}>
              // PREISE NICHT LADBAR
            </span>
          </div>
          <div className="notice-body">
            <div className="mono" style={{ color: "var(--bad)" }}>
              {state.error.message}
            </div>
            <div>
              <button type="button" className="btn" onClick={refetch}>
                <Icon name="refresh" size={14} /> Nochmal versuchen
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!preise || preise.positionen.length === 0) {
    return (
      <div className="ueber">
        <section className="block">
          <div className="block-head">
            <span className="uppercase-mini">// PREISE</span>
          </div>
          <div className="notice-body">
            <div className="dim">Phase 5 hat keine Positionen. Pipeline noch nicht durch?</div>
          </div>
        </section>
      </div>
    );
  }

  const bepreisbar = preise.positionen.filter((p) => p.bepreisbar).length;

  function toggleGroup(path: string) {
    setClosedGroups((cur) => {
      const next = new Set(cur);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  return (
    <div className="ueber">
      <section className="block">
        <div className="block-head">
          <span className="uppercase-mini">
            // PREISE · {preise.positionen.length} POSITIONEN
          </span>
          <span className="block-sep" />
          <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
            <span className="mono" style={{ color: "var(--fg)" }}>
              {bepreisbar}
            </span>{" "}
            bepreisbar · Klick = Override
          </span>
        </div>
        <div className="preise-table">
          <div className="preise-head uppercase-mini">
            <div>OZ</div>
            <div>Artikel</div>
            <div className="num">Liste</div>
            <div className="num">Rabatt</div>
            <div className="num">Sonder</div>
            <div className="num">VK</div>
            <div className="num">Marge</div>
          </div>
          {tree.map((group) => (
            <PreisGroup
              key={group.path || "_root"}
              group={group}
              closedGroups={closedGroups}
              onToggle={toggleGroup}
              onEdit={(p) => p.bepreisbar && setEditing(p)}
            />
          ))}
        </div>
        <div className="pe-summary">
          <span>
            Summe netto:{" "}
            <strong className="mono" style={{ color: "var(--acc-hot)" }}>
              {fmtEur(preise.summeNetto, preise.waehrung)}
            </strong>
          </span>
          <span className="dim">
            {bepreisbar} / {preise.positionen.length} bepreisbar
          </span>
        </div>
      </section>

      {editing && (
        <PreisEditDialog
          tenantId={tenantId}
          anfrageId={anfrageId}
          position={editing}
          menge={konsolMap.get(editing.positionId)?.menge ?? 1}
          einheit={konsolMap.get(editing.positionId)?.einheit ?? ""}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setLocal(updated);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

interface PreisRow {
  preis: PreisPosition;
  konsol: KonsolidiertePosition | null;
}

function PreisGroup({
  group,
  closedGroups,
  onToggle,
  onEdit,
}: {
  group: OzGroupNode<PreisRow>;
  closedGroups: Set<string>;
  onToggle: (path: string) => void;
  onEdit: (p: PreisPosition) => void;
}) {
  const hasHeader = group.path !== "";
  const isOpen = !hasHeader || !closedGroups.has(group.path);
  const total = countItems(group);

  if (!hasHeader) {
    return (
      <>
        {group.items.map((row, i) => (
          <PreisRow
            key={row.preis.positionId}
            row={row}
            onEdit={onEdit}
            animDelay={i * 22}
          />
        ))}
        {group.children.map((child) => (
          <PreisGroup
            key={child.path}
            group={child}
            closedGroups={closedGroups}
            onToggle={onToggle}
            onEdit={onEdit}
          />
        ))}
      </>
    );
  }

  const title = group.hauptgruppe ?? `Gruppe ${group.path}`;
  // Summe netto pro Gruppe
  const sum = sumGroupTotals(group);

  return (
    <>
      <button
        type="button"
        onClick={() => onToggle(group.path)}
        className="preise-row"
        style={{
          background: "var(--bg-raised)",
          borderTop: "1px solid var(--line-soft)",
          borderBottom: "1px solid var(--line-soft)",
          cursor: "pointer",
          width: "100%",
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          marginTop: group.depth === 0 ? 8 : 0,
        }}
      >
        <div className="mono" style={{ color: "var(--fg-dim)", fontSize: 12 }}>
          {isOpen ? "▾" : "▸"} {group.path}
        </div>
        <div
          className="preise-text"
          style={{
            fontWeight: 600,
            color: "var(--fg)",
            gridColumn: "span 3",
          }}
        >
          {title}{" "}
          <span className="mono dim" style={{ fontSize: 11, marginLeft: 8 }}>
            · {total} Pos
          </span>
        </div>
        <div />
        <div className="num mono" style={{ color: "var(--acc)" }}>
          {fmtEur(sum, "EUR")}
        </div>
        <div />
      </button>
      {isOpen && (
        <>
          {group.items.map((row, i) => (
            <PreisRow
              key={row.preis.positionId}
              row={row}
              onEdit={onEdit}
              animDelay={i * 22}
            />
          ))}
          {group.children.map((child) => (
            <PreisGroup
              key={child.path}
              group={child}
              closedGroups={closedGroups}
              onToggle={onToggle}
              onEdit={onEdit}
            />
          ))}
        </>
      )}
    </>
  );
}

function PreisRow({
  row,
  onEdit,
  animDelay,
}: {
  row: PreisRow;
  onEdit: (p: PreisPosition) => void;
  animDelay: number;
}) {
  const p = row.preis;
  const k = row.konsol;
  const isSchnellkalk = p.quelle === "schnellkalkulation";
  return (
    <div
      className="preise-row"
      style={{
        animationDelay: `${animDelay}ms`,
        cursor: p.bepreisbar && !isSchnellkalk ? "pointer" : "default",
        opacity: p.bepreisbar ? 1 : 0.5,
      }}
      onClick={() => p.bepreisbar && !isSchnellkalk && onEdit(p)}
      title={
        isSchnellkalk
          ? "Schnellkalkulation — Bearbeitung im Match-Tab"
          : p.bepreisbar
            ? "Klick: Preis anpassen"
            : "nicht bepreisbar"
      }
    >
      <div className="mono" style={{ color: "var(--fg-mute)" }}>
        {k?.oz ?? p.positionId}
      </div>
      <div className="preise-text" style={{ fontSize: 12 }}>
        <span className="mono" style={{ color: "var(--fg)" }}>
          {isSchnellkalk ? "Schnellkalk." : (p.artikelCode ?? "—")}
        </span>
        {k && (
          <span
            style={{
              marginLeft: 10,
              color: "var(--fg-dim)",
              fontSize: 12,
            }}
          >
            {k.originaltext}
          </span>
        )}
        {isSchnellkalk && <AnteilPills p={p} />}
      </div>
      <div className="num mono dim">{fmt(p.listenpreis)}</div>
      <div className="num mono dim">
        {p.rabattProzent > 0 ? `${p.rabattProzent}%` : "—"}
      </div>
      <div className="num mono dim">{fmt(p.sonderpreis)}</div>
      <div className="num mono">{fmt(p.einzelpreis)}</div>
      <div className="num mono">
        {p.margeProzent > 0 ? (
          <span style={{ color: "var(--acc)" }}>{p.margeProzent}%</span>
        ) : (
          <span className="dim">—</span>
        )}
      </div>
    </div>
  );
}

function AnteilPills({ p }: { p: PreisPosition }) {
  const items: Array<{ key: string; label: string; val: number | null; tone: string }> = [
    { key: "lohn", label: "L", val: p.anteilLohn, tone: "var(--acc-hot)" },
    { key: "material", label: "M", val: p.anteilMaterial, tone: "var(--ok)" },
    { key: "geraet", label: "G", val: p.anteilGeraet, tone: "var(--acc)" },
    { key: "sonstige", label: "S", val: p.anteilSonstige, tone: "var(--warn)" },
    { key: "fremd", label: "F", val: p.anteilFremd, tone: "var(--fg-dim)" },
  ];
  return (
    <span style={{ marginLeft: 10, display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {items
        .filter((i) => i.val !== null && i.val > 0)
        .map((i) => (
          <span
            key={i.key}
            className="pill"
            style={{
              borderColor: i.tone,
              color: i.tone,
              padding: "1px 6px",
              fontSize: 10,
            }}
            title={`${i.key}: ${fmt(i.val)} €`}
          >
            <span style={{ fontWeight: 600, marginRight: 3 }}>{i.label}</span>
            <span className="mono">{fmt(i.val)}</span>
          </span>
        ))}
    </span>
  );
}

function sumGroupTotals(group: OzGroupNode<PreisRow>): number {
  let sum = 0;
  for (const it of group.items) sum += it.preis.gesamtpreis ?? 0;
  for (const ch of group.children) sum += sumGroupTotals(ch);
  return sum;
}

function fmt(n: number | null): string {
  if (n === null) return "—";
  return n.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtEur(n: number, waehrung: string): string {
  return `${fmt(n)} ${waehrung}`;
}
